import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Skeleton, Toggle, useToast } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { productMasterService } from '@/services/product-master.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/constants/product';
import { GLASS, cn } from '@/constants/design-tokens';
import { formatPaise, formatNumber, marginPercent } from '@/utils/currency';
import { formatPct, isBelowMinMargin, lowestPriceForMargin } from '@/utils/margin';
import { paiseToRupeeInput, rupeeInputToPaise } from '@/utils/price-input';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { MasterStoreRow, MasterVariant, ProductMasterDetail } from '@/types/product-master';

export const masterQueryKey = (masterId: string) => ['product-master', masterId] as const;

/** "+₹10.00 (+8.3%)": a store's own price against the base (master) price. */
function vsBase(price: number, base: number, currency: CurrencyCode): string {
  const diff = price - base;
  if (diff === 0) return 'Same as base';
  const sign = diff > 0 ? '+' : '−';
  const pct = base > 0 ? ` (${sign}${((Math.abs(diff) / base) * 100).toFixed(1)}%)` : '';
  return `${sign}${formatPaise(Math.abs(diff), currency)}${pct}`;
}

/**
 * "Cost ₹90.00 · Margin 30.8%" for one store row, and an amber "Below 25%
 * minimum" pill when its price leaves less than the business minimum (C09,
 * G-G1 — a warning only). Cost 0 / unknown: "—", never a warning.
 */
function MarginLine({
  price,
  cost,
  minPct,
  currency,
}: {
  price: number;
  cost: number | null;
  minPct: number | null;
  currency: CurrencyCode;
}) {
  const hasCost = cost !== null && cost > 0;
  const below = isBelowMinMargin(price, cost, minPct);
  const lowest = lowestPriceForMargin(cost, minPct);
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
      <span className="tabular-nums">{`Cost ${hasCost ? formatPaise(cost, currency) : '—'}`}</span>
      <span aria-hidden="true">·</span>
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 font-semibold tabular-nums transition-all duration-200',
          below
            ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
            : 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300',
        )}
      >
        {`Margin ${hasCost && price > 0 ? `${marginPercent(price, cost)}%` : '—'}`}
      </span>
      {below && lowest !== null && minPct !== null && (
        <span
          title={`Lowest price for a ${formatPct(minPct)} margin: ${formatPaise(lowest, currency)}`}
          aria-label={`Below ${formatPct(minPct)} minimum. Lowest price for a ${formatPct(minPct)} margin: ${formatPaise(lowest, currency)}`}
          className="inline-flex items-center rounded-full bg-warning-500 px-2 py-0.5 font-semibold text-white shadow-sm dark:bg-warning-600"
        >
          {`Below ${formatPct(minPct)} minimum`}
        </span>
      )}
    </p>
  );
}

/** "M / Red" from a variant's attributes, else its name. */
function variantLabel(variant: MasterVariant): string {
  const values = Object.values(variant.variant_attributes ?? {}).map(String).filter(Boolean);
  return values.length > 0 ? values.join(' / ') : variant.name;
}

function SoldPill({ sold }: { sold: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        sold
          ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
          : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', sold ? 'bg-success-500' : 'bg-neutral-400')} />
      {sold ? 'Sold here' : 'Not sold'}
    </span>
  );
}

/**
 * The stores a person may switch a product on or off at: an Owner at every
 * store, a Manager only at the stores they are assigned to (plan D5). The API
 * checks the same rule; this only decides which rows show a toggle.
 */
export function useManageableStore() {
  const profile = useAuthStore((s) => s.profile);
  const isOwner = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.OWNER;
  const assigned = new Set((profile?.locations ?? []).map((l) => l.id));
  return (locationId: string) => isOwner || assigned.has(locationId);
}

function patchStore(
  master: ProductMasterDetail | undefined,
  locationId: string,
  change: Partial<MasterStoreRow>,
): ProductMasterDetail | undefined {
  if (!master) return master;
  return {
    ...master,
    stores: master.stores.map((s) => (s.location_id === locationId ? { ...s, ...change } : s)),
  };
}

function AvailablePill({ available }: { available: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        available
          ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
          : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', available ? 'bg-teal-500' : 'bg-danger-500')} />
      {available ? 'Available' : 'Sold out'}
    </span>
  );
}

/**
 * One row per store for a master product: sold here or not, stock, the price
 * the store charges, and the store's own price with a reset (plan §11). Used
 * on the product page and in the Master catalogue.
 *
 * `menu` is the restaurant form (plan §17, R6), opened from the Menus page: a
 * dish's stock lives in its ingredients, so each outlet shows whether the dish
 * is available or sold out there (86), with a switch, instead of a stock count.
 */
export function MasterStoresPanel({
  masterId,
  className,
  menu = false,
}: {
  masterId: string;
  className?: string;
  menu?: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const canManage = useManageableStore();
  const isOwner = useAuthStore((s) => (ROLE_LEVELS[s.profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.OWNER);
  const key = masterQueryKey(masterId);
  // Own-price inputs the user has typed into but not saved, by store.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const { data: master, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => productMasterService.getById(masterId),
  });

  function refreshLists() {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['product'] });
    queryClient.invalidateQueries({ queryKey: ['product-masters'] });
  }

  const toggle = useMutation({
    mutationFn: ({ store, enable }: { store: MasterStoreRow; enable: boolean }) =>
      productMasterService.setLocations(
        masterId,
        enable ? { enable: [store.location_id] } : { disable: [store.location_id] },
      ),
    // Flip the switch at once; put it back if the API says no.
    onMutate: async ({ store, enable }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductMasterDetail>(key);
      queryClient.setQueryData<ProductMasterDetail>(key, (m) =>
        patchStore(m, store.location_id, {
          enabled: enable,
          stock_quantity: store.stock_quantity ?? (enable ? 0 : null),
        }),
      );
      return { previous };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      toast({ title: err.message || 'Could not change this store', variant: 'error' });
    },
    onSuccess: (_res, { store, enable }) => {
      toast({
        title: enable ? `Now sold at ${store.location_name}` : `No longer sold at ${store.location_name}`,
        variant: 'success',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      refreshLists();
    },
  });

  // One variant on or off at one store. Switching the parent already takes its variants along (API).
  const toggleVariant = useMutation({
    mutationFn: ({ variant, store, enable }: { variant: MasterVariant; store: MasterStoreRow; enable: boolean }) =>
      productMasterService.setLocations(
        variant.id,
        enable ? { enable: [store.location_id] } : { disable: [store.location_id] },
      ),
    onError: (err: Error) => toast({ title: err.message || 'Could not change this variant', variant: 'error' }),
    onSuccess: (_res, { variant, store, enable }) => {
      toast({
        title: `${variantLabel(variant)} ${enable ? 'now sold' : 'no longer sold'} at ${store.location_name}`,
        variant: 'success',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      refreshLists();
    },
  });

  // 86 a dish at one outlet; the other outlets keep their own state (plan §17).
  const setAvailable = useMutation({
    mutationFn: ({ store, available }: { store: MasterStoreRow; available: boolean }) =>
      productMasterService.setMenuAvailability(store.product_id as string, available),
    onMutate: async ({ store, available }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductMasterDetail>(key);
      queryClient.setQueryData<ProductMasterDetail>(key, (m) =>
        patchStore(m, store.location_id, { is_available: available }),
      );
      return { previous };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      toast({ title: err.message || 'Could not change this location', variant: 'error' });
    },
    onSuccess: (_res, { store, available }) => {
      toast({
        title: available ? `Available again at ${store.location_name}` : `Sold out at ${store.location_name}`,
        variant: 'success',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      refreshLists();
    },
  });

  const setPrice = useMutation({
    mutationFn: ({ store, price }: { store: MasterStoreRow; price: number | null }) =>
      productMasterService.setStorePrice(masterId, store.location_id, price),
    onSuccess: (res, { store, price }) => {
      // The minimum margin only warns (C09, G-G1): the price is saved either way.
      const minPct = queryClient.getQueryData<ProductMasterDetail>(key)?.min_margin_pct ?? null;
      const below = !menu && price !== null && minPct !== null && isBelowMinMargin(res.price, store.cost, minPct);
      queryClient.setQueryData<ProductMasterDetail>(key, (m) =>
        patchStore(m, store.location_id, { price: res.price, price_overridden: res.price_overridden }),
      );
      setDrafts((d) => {
        const next = { ...d };
        delete next[store.location_id];
        return next;
      });
      toast({
        title:
          price === null
            ? `${store.location_name} uses the master price again`
            : below && minPct !== null
              ? `Own price saved for ${store.location_name}. It is below your ${formatPct(minPct)} minimum margin.`
              : `Own price saved for ${store.location_name}`,
        variant: below ? 'warning' : 'success',
      });
      refreshLists();
    },
    onError: (err: Error) => {
      toast({ title: err.message || 'Could not save the price', variant: 'error' });
    },
  });

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={56} />
        ))}
      </div>
    );
  }
  if (error || !master) {
    return (
      <div className={cn(GLASS, 'p-6 text-center text-sm text-danger-600 dark:text-danger-400', className)}>
        Could not load the stores for this product.
      </div>
    );
  }

  const stores = master.stores;
  const variants = master.variants ?? [];
  // Each variant has its own price (OD5), so a parent with variants has no price to show or set.
  const perVariant = variants.length > 0;
  const sellingCount = stores.filter((s) => s.enabled).length;
  const anyLocked = stores.some((s) => !canManage(s.location_id));
  const minPct = master.min_margin_pct ?? null;

  function savePrice(store: MasterStoreRow) {
    const typed = drafts[store.location_id] ?? '';
    const price = rupeeInputToPaise(typed, currency);
    if (price === null || price < 0) {
      toast({ title: 'Enter a price of 0 or more', variant: 'warning' });
      return;
    }
    setPrice.mutate({ store, price });
  }

  return (
    <div className={cn(GLASS, 'overflow-hidden animate-fade-in', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 bg-gradient-to-r from-teal-50 to-emerald-50 px-4 py-3 dark:border-neutral-700 dark:from-teal-900/20 dark:to-emerald-900/10">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">{menu ? 'Locations' : 'Stores'}</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {perVariant ? (
              'Priced per variant'
            ) : (
              <>
                Master price{' '}
                <span className="font-semibold tabular-nums text-neutral-700 dark:text-neutral-200">
                  {formatPaise(master.default_price, currency)}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {variants.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
              {variants.length} {variants.length === 1 ? 'variant' : 'variants'}
            </span>
          )}
          {!menu && minPct !== null &&
            (isOwner ? (
              <Link
                to="/settings/pricing"
                className="inline-flex items-center rounded-full bg-warning-100 px-2.5 py-1 text-xs font-semibold text-warning-700 transition-all duration-200 hover:bg-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:hover:bg-warning-900/50"
              >
                {`Min margin ${formatPct(minPct)}`}
              </Link>
            ) : (
              <span className="inline-flex items-center rounded-full bg-warning-100 px-2.5 py-1 text-xs font-semibold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
                {`Min margin ${formatPct(minPct)}`}
              </span>
            ))}
          <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-1 text-xs font-semibold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
            {menu ? `On the menu at ${sellingCount} / ${stores.length} locations` : `Sold at ${sellingCount} / ${stores.length} stores`}
          </span>
        </div>
      </div>

      {variants.length > 0 && (
        <p className="border-b border-neutral-100 px-4 py-2 text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          Switching the product on or off at a store switches its variants with it.
        </p>
      )}

      {menu && (
        <p className="border-b border-neutral-100 px-4 py-2 text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          The recipe, description and food type are the same at every location. Each location keeps its own
          ingredient stock, and can mark the dish sold out on its own.
        </p>
      )}

      {anyLocked && (
        <p className="border-b border-neutral-100 px-4 py-2 text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          You can change only the {menu ? 'locations' : 'stores'} you are assigned to.
        </p>
      )}

      {/* Column headings (wide screens; each row labels its own cells on a phone) */}
      <div className="hidden grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.6fr)_minmax(0,1.8fr)] gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 sm:grid">
        <span>{menu ? 'Location' : 'Store'}</span>
        <span>{menu ? 'On the menu' : 'Sells it'}</span>
        <span className={menu ? undefined : 'text-right'}>{menu ? 'Today' : 'Stock'}</span>
        <span>Price</span>
      </div>

      <ul role="list" className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {stores.map((store) => {
          const manage = canManage(store.location_id);
          const draft = drafts[store.location_id];
          // C09: check a typed own price against the minimum margin as it is typed. Save stays enabled.
          const draftPrice = draft !== undefined ? rupeeInputToPaise(draft, currency) : null;
          const draftCost = store.cost ?? null;
          const liveCheck =
            !menu && !perVariant && draftPrice !== null && draftPrice > 0 && draftCost !== null && draftCost > 0;
          const liveBelow = liveCheck && isBelowMinMargin(draftPrice, draftCost, minPct);
          const liveLowest = lowestPriceForMargin(draftCost, minPct);
          const busy =
            (toggle.isPending && toggle.variables?.store.location_id === store.location_id) ||
            (setPrice.isPending && setPrice.variables?.store.location_id === store.location_id) ||
            (setAvailable.isPending && setAvailable.variables?.store.location_id === store.location_id);
          const available = store.is_available !== false;
          return (
            <li
              key={store.location_id}
              data-testid={`store-row-${store.location_id}`}
              className="grid grid-cols-1 gap-2 px-4 py-3 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 animate-slide-up dark:even:bg-neutral-800/40 dark:hover:bg-brand-900/20 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.6fr)_minmax(0,1.8fr)] sm:items-center sm:gap-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {store.location_name}
                </p>
                {store.location_code && (
                  <p className="text-xs text-neutral-400 dark:text-neutral-500">{store.location_code}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {manage ? (
                  <Toggle
                    size="sm"
                    checked={store.enabled}
                    disabled={busy}
                    label={store.enabled ? 'Sold here' : 'Not sold'}
                    onChange={(enable) => toggle.mutate({ store, enable })}
                  />
                ) : (
                  <SoldPill sold={store.enabled} />
                )}
              </div>

              {menu ? (
                <div className="flex items-center justify-between gap-2 sm:justify-start">
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 sm:hidden">Today</span>
                  {!store.enabled ? (
                    <span className="text-sm text-neutral-400 dark:text-neutral-500">—</span>
                  ) : manage && store.product_id ? (
                    <Toggle
                      size="sm"
                      checked={available}
                      disabled={busy}
                      label={available ? 'Available' : 'Sold out'}
                      onChange={(next) => setAvailable.mutate({ store, available: next })}
                    />
                  ) : (
                    <AvailablePill available={available} />
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between text-sm tabular-nums text-neutral-700 dark:text-neutral-300 sm:justify-end">
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 sm:hidden">Stock</span>
                  {store.enabled ? formatNumber(store.stock_quantity ?? 0) : '—'}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {perVariant ? (
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    {store.enabled ? 'Priced per variant' : '—'}
                  </span>
                ) : store.enabled && store.price !== null ? (
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                        {formatPaise(store.price, currency)}
                      </span>
                      {store.price_overridden && (
                        <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                          Own price
                        </span>
                      )}
                    </div>
                    {/* C09: the read-only base price, and how far the store's own price is from it. */}
                    <p className="flex flex-wrap items-center gap-x-2 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
                      <span>{`Base ${formatPaise(master.default_price, currency)}`}</span>
                      {store.price_overridden && (
                        <span
                          className={cn(
                            'font-semibold',
                            store.price >= master.default_price
                              ? 'text-success-600 dark:text-success-400'
                              : 'text-warning-600 dark:text-warning-400',
                          )}
                        >
                          {vsBase(store.price, master.default_price, currency)}
                        </span>
                      )}
                    </p>
                    {/* A dish's cost is in its recipe, so the menu form shows no cost or margin. */}
                    {!menu && (
                      <MarginLine price={store.price} cost={store.cost ?? null} minPct={minPct} currency={currency} />
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-neutral-400 dark:text-neutral-500">—</span>
                )}
                {manage && store.enabled && !perVariant && (
                  <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
                    <div className="w-28">
                      <Input
                        type="number"
                        size="sm"
                        inputMode="decimal"
                        aria-label={`Own price at ${store.location_name}`}
                        placeholder="Own price"
                        value={draft ?? (store.price_overridden ? paiseToRupeeInput(store.price, currency) : '')}
                        onChange={(v) => setDrafts((d) => ({ ...d, [store.location_id]: v }))}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy || draft === undefined || draft.trim() === ''}
                      onClick={() => savePrice(store)}
                    >
                      Save
                    </Button>
                    {store.price_overridden && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => setPrice.mutate({ store, price: null })}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                )}
                {manage && store.enabled && liveCheck && (
                  <p
                    role="status"
                    className={cn(
                      'w-full text-xs transition-all duration-200 sm:text-right',
                      liveBelow
                        ? 'font-medium text-warning-700 dark:text-warning-300'
                        : 'text-neutral-500 dark:text-neutral-400',
                    )}
                  >
                    {`This leaves a ${marginPercent(draftPrice as number, draftCost as number)}% margin.`}
                    {liveBelow && minPct !== null && liveLowest !== null
                      ? ` Your minimum is ${formatPct(minPct)} (${formatPaise(liveLowest, currency)} or more).`
                      : ''}
                  </p>
                )}
              </div>

              {variants.length > 0 && store.product_id && (
                <ul
                  role="list"
                  aria-label={`Variants at ${store.location_name}`}
                  className="space-y-1 border-l-2 border-violet-200 pl-3 dark:border-violet-800 sm:col-span-4 sm:ml-4"
                >
                  {variants.map((variant) => {
                    const at = variant.stores.find((s) => s.location_id === store.location_id);
                    const sold = Boolean(at?.enabled);
                    const variantBusy =
                      toggleVariant.isPending &&
                      toggleVariant.variables?.variant.id === variant.id &&
                      toggleVariant.variables?.store.location_id === store.location_id;
                    return (
                      <li
                        key={variant.id}
                        data-testid={`variant-row-${variant.id}-${store.location_id}`}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                      >
                        <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                          {variantLabel(variant)}
                        </span>
                        <span className="text-xs tabular-nums text-neutral-400 dark:text-neutral-500">{variant.sku}</span>
                        <span className="ml-auto text-xs tabular-nums text-neutral-600 dark:text-neutral-300">
                          {sold ? `Stock ${formatNumber(at?.stock_quantity ?? 0)}` : at ? 'Off here' : 'Not at this store'}
                        </span>
                        {manage && store.enabled ? (
                          <Toggle
                            size="sm"
                            checked={sold}
                            disabled={variantBusy}
                            label={`${variantLabel(variant)} at ${store.location_name}`}
                            onChange={(enable) => toggleVariant.mutate({ variant, store, enable })}
                          />
                        ) : (
                          <SoldPill sold={sold} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
